import React, { useContext, useState, useEffect } from "react";
import { updateDoc, doc, onSnapshot, collection, query, where, getDocs, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import UserContext from "../context/UserContext";

const Profile = () => {
  const { user, loading } = useContext(UserContext);
  const [username, setUsername] = useState("");
  const [ageGroup, setAgeGroup] = useState("Kids");
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [initialUsername, setInitialUsername] = useState(""); // Store initial username for comparison
  const [initialAgeGroup, setInitialAgeGroup] = useState("Kids"); // Store initial age group

  // Fetch user data from Firestore when component mounts
  useEffect(() => {
    if (user) {
      const userRef = doc(db, "users", user.uid);
      const unsubscribe = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUsername(userData.username || "");
          setInitialUsername(userData.username || ""); // Set initial username
          setAgeGroup(userData.age_group || "Kids");
          setInitialAgeGroup(userData.age_group || "Kids"); // Set initial age group
        } else {
          setMessage("User document not found.");
        }
      }, (error) => {
        // Handle permission errors gracefully, especially during logout
        if (error.code === 'permission-denied') {
          console.warn('Permission denied accessing user profile data, likely due to logout');
          setUsername("");
          setAgeGroup("Kids");
          setMessage("Session expired. Please log in again.");
        } else {
          console.error("Error fetching user data:", error);
          setMessage("Error loading profile data: " + error.message);
        }
      });
      return () => unsubscribe(); // Cleanup subscription
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    console.log("handleSave called, saving changes:", { username, age_group: ageGroup }); // Debug log
    if (!user) {
      setMessage("No user logged in.");
      return;
    }

    // Check if username has changed and if it's already taken
    if (username !== initialUsername) {
      try {
        console.log("Checking username availability using Cloud Function...");
        console.log("Current user:", user);
        console.log("User authenticated:", user ? "Yes" : "No");

        // Use Cloud Function for username checking
        console.log("Username to check:", `"${username}"`);
        console.log("Username type:", typeof username);
        console.log("Username length:", username ? username.length : 'null/undefined');

        const checkUsername = httpsCallable(functions, 'checkUsernameAvailability');
        console.log("Calling checkUsernameAvailability function...");
        const result = await checkUsername({ username });
        console.log("Cloud Function result:", result);

        if (!result.data.available) {
          setMessage("Username is already taken. Please choose a different one.");
          return;
        }

        console.log("Username is available!");
      } catch (err) {
        console.error("Username check error:", err);
        console.error("Error code:", err.code);
        console.error("Error message:", err.message);
        setMessage("Error checking username availability. Please try again.");
        return;
      }
    }

    const userRef = doc(db, "users", user.uid);
    let newAgeGroup = ageGroup;

    try {
      console.log("Attempting updateDoc with:", { username, age_group: newAgeGroup }); // Debug log
      const updateData = { username, age_group: newAgeGroup };
      console.log("Update data structure:", updateData); // Debug data structure
      await updateDoc(userRef, updateData);
      setInitialUsername(username); // Update initial username after successful save
      setInitialAgeGroup(newAgeGroup); // Update initial age group
      setIsEditing(false);
      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000); // Clear message after 3 seconds
    } catch (err) {
      console.error("Update error:", err); // Debug error
      setMessage("Error updating profile: " + err.message);
      setUsername(initialUsername); // Revert username on error
      setAgeGroup(initialAgeGroup); // Revert age group on error
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage("");
  };

  const handleCancel = () => {
    setUsername(initialUsername); // Revert to initial username
    setAgeGroup(initialAgeGroup); // Revert to initial age group
    setIsEditing(false);
    setMessage("");
  };

  if (loading) return <div className="text-gray-800">Loading...</div>;
  if (!user) return <div className="text-gray-800">Please log in to view your profile.</div>;

  return (
    <div className={`p-4 max-w-4xl mx-auto min-h-screen ${ageGroup.toLowerCase()}-theme`} data-age-group={ageGroup}>
      <h1 className="text-3xl font-bold mb-4">Profile Settings</h1>
      <div className="bg-silver p-4 rounded-custom shadow-lg">
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-gray-800">Username</label>
            {isEditing ? (
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-2 bg-silver text-gray-800 rounded-custom border-2 border-gray-600"
              />
            ) : (
              <p className="p-2 bg-silver text-gray-800 rounded-custom border-2 border-gray-600">{username || "No username set"}</p>
            )}
          </div>
          <div>
            <label className="block text-gray-800">Age Group</label>
            <select
              value={ageGroup}
              onChange={(e) => setAgeGroup(e.target.value)}
              className="w-full p-2 bg-silver text-gray-800 rounded-custom border-2 border-gray-600"
              disabled={!isEditing}
            >
              <option value="Kids">Kids (1-12)</option>
              <option value="Teens">Teens (13-17)</option>
              <option value="Adults">Adults (18+)</option>
            </select>
          </div>
          {isEditing ? (
            <div className="space-x-2">
              <button
                type="submit"
                className="bg-kids-blue text-white px-4 py-2 rounded-custom hover:bg-blue-600"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-500 text-white px-4 py-2 rounded-custom hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleEdit}
              className="bg-green-500 text-white px-4 py-2 rounded-custom hover:bg-green-600"
            >
              Edit
            </button>
          )}
          {message && <p className="mt-2 text-gray-800">{message}</p>}
        </form>
      </div>
    </div>
  );
};

export default Profile;
